/**
 * GATE-READINESS-FE-2. A phase is gated when the project has use_gates on
 * AND that phase's plan gate is armed (`gate.definitionExists`). BE-2 records
 * an unfinished move as a deferral when the body is `{ phaseId, closureReason }`
 * with no closureKind, and a cancel as `{ status: 'CANCELED', closureKind, closureReason }`.
 */

export type ClosurePhase = {
  id: string;
  name: string;
  sortOrder: number;
  gateDefinitionExists: boolean;
};

type PlanPhaseRaw = {
  id: string;
  name?: string;
  sortOrder?: number;
  sort_order?: number;
  gate?: { definitionExists?: boolean; definition_exists?: boolean } | null;
};

export function phaseFromPlan(raw: PlanPhaseRaw): ClosurePhase {
  const sort = raw.sortOrder ?? raw.sort_order ?? 0;
  const gate = raw.gate ?? null;
  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    sortOrder: typeof sort === 'number' ? sort : Number(sort) || 0,
    gateDefinitionExists:
      gate?.definitionExists === true || gate?.definition_exists === true,
  };
}

export function useGatesFromPlan(raw: { capabilities?: { use_gates?: boolean } } | null | undefined): boolean | null {
  const value = raw?.capabilities?.use_gates;
  return typeof value === 'boolean' ? value : null;
}

/** Open-bucket tasks. Done and cancelled move without a deferral reason. */
export function isUnfinishedTaskStatus(status: string): boolean {
  return status !== 'DONE' && status !== 'CANCELED';
}

export function closureRequiredForPhase(
  useGates: boolean,
  phase: { gateDefinitionExists?: boolean } | undefined,
): boolean {
  return useGates === true && phase?.gateDefinitionExists === true;
}

export function nextClosurePhase(
  phases: readonly ClosurePhase[],
  currentPhaseId: string,
): ClosurePhase | null {
  const current = phases.find((phase) => phase.id === currentPhaseId);
  if (!current) return null;
  let next: ClosurePhase | null = null;
  for (const phase of phases) {
    if (phase.sortOrder <= current.sortOrder) continue;
    if (next === null || phase.sortOrder < next.sortOrder) next = phase;
  }
  return next;
}

export function deferralDecision(
  phases: readonly ClosurePhase[],
  task: { status: string; phaseId?: string | null },
  useGates: boolean,
): { required: false } | { required: true; next: ClosurePhase | null } {
  const phase = phases.find((item) => item.id === task.phaseId);
  if (!isUnfinishedTaskStatus(task.status) || !closureRequiredForPhase(useGates, phase) || !task.phaseId) {
    return { required: false };
  }
  return { required: true, next: nextClosurePhase(phases, task.phaseId) };
}

export function moveMenuPhases(
  phases: readonly ClosurePhase[],
  task: { status: string; phaseId?: string | null },
  useGates: boolean,
): { phases: ClosurePhase[]; deferralBlocked: boolean } {
  const decision = deferralDecision(phases, task, useGates);
  if (!decision.required) return { phases: [...phases], deferralBlocked: false };
  if (!decision.next) return { phases: [], deferralBlocked: true };
  return { phases: [decision.next], deferralBlocked: false };
}

export function cancelClosureBody(
  kind: 'CANCELLED' | 'NOT_APPLICABLE',
  reason: string,
): { status: 'CANCELED'; closureKind: 'CANCELLED' | 'NOT_APPLICABLE'; closureReason: string } {
  return { status: 'CANCELED', closureKind: kind, closureReason: reason };
}

export function deferralClosureBody(
  phaseId: string,
  reason: string,
): { phaseId: string; closureReason: string } {
  return { phaseId, closureReason: reason };
}

/**
 * PATCH /api/work/tasks/:id 409 from GATE-EXECUTION-LOCK-1 ruling 5.
 * The wire body is `{ code, message, fromStatus, toStatus, gatePhaseId?, gateDefinitionId? }`.
 */
export function isExecutionLockResetAckRequired(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const response = (err as { response?: { status?: unknown; data?: { code?: unknown } } }).response;
  return response?.status === 409 && response.data?.code === 'EXECUTION_LOCK_RESET_ACK_REQUIRED';
}
